import { useLiveQuery } from 'dexie-react-hooks';
import { DevisRecord, nextDevisNumber, readDevisStore, saveDevisStore } from '@shared/domain/devis';
import { hubDb } from '@shared/storage/db';

function sortDevis(records: DevisRecord[]) {
  return [...records].sort((a, b) => (b.updatedAt || '').localeCompare(a.updatedAt || ''));
}

async function mirrorDevisToLocalStorage() {
  const records = await hubDb.devis.toArray();
  saveDevisStore(sortDevis(records));
}

export async function migrateLegacyDevisIfNeeded() {
  const count = await hubDb.devis.count();
  if (count > 0) {
    return;
  }

  const legacy = sortDevis(readDevisStore());
  if (!legacy.length) {
    return;
  }

  await hubDb.devis.bulkPut(legacy);
  await mirrorDevisToLocalStorage();
}

export async function listDevis() {
  const records = await hubDb.devis.toArray();
  return sortDevis(records);
}

export async function getDevisById(id: string) {
  return hubDb.devis.get(id);
}

export async function upsertDevisRecord(record: DevisRecord) {
  await migrateLegacyDevisIfNeeded();
  await hubDb.devis.put(record);
  await mirrorDevisToLocalStorage();
  return record;
}

export function useDevis(): DevisRecord[] {
  return useLiveQuery(
    () => hubDb.devis.orderBy('updatedAt').reverse().toArray(),
    [],
    [] as DevisRecord[],
  ) ?? [];
}

export async function removeDevisRecord(id: string) {
  await migrateLegacyDevisIfNeeded();
  await hubDb.devis.delete(id);
  await mirrorDevisToLocalStorage();
}

export async function duplicateDevisRecord(record: DevisRecord) {
  const existing = await listDevis();
  const duplicate: DevisRecord = {
    ...record,
    id: `devis_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    num: nextDevisNumber(existing),
    status: 'brouillon',
    date: new Date().toISOString().slice(0, 10),
    validUntil: (() => {
      const date = new Date();
      date.setDate(date.getDate() + 30);
      return date.toISOString().slice(0, 10);
    })(),
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  await upsertDevisRecord(duplicate);
  return duplicate;
}
